// src/services/address/factory-helpers.js
// *******************************************************************************
// FACTORY HELPER (CORE) - Reusable for Any Integration
// *******************************************************************************
// Generic fallback logic - can be reused for SMS, Translation, Payment, CEP, etc.
// Adapted from backend pattern: src/modules/integrations/factory-helpers.js
// *******************************************************************************

/**
 * Default success checker
 * @param {*} result - Result from provider
 * @returns {boolean} - true if result is considered successful
 */
function defaultIsLogicalSuccess(result) {
  if (!result) return false;

  // Arrays (ex: multiple results)
  if (Array.isArray(result)) {
    return result.some(r => r && r.success === true);
  }

  // Objects with array of results
  if (typeof result === 'object') {
    if (Array.isArray(result.results)) {
      return result.results.some(r => r && r.success === true);
    }
    if ('success' in result) return !!result.success;
    if ('ok' in result) return !!result.ok;
  }

  return false;
}

/**
 * Ensures adapter implements required contract
 * @param {Object} adapter - Adapter object
 * @param {Array<string>} methods - Required method names
 * @param {string} providerName - Provider name for error messages
 * @throws {Error} If adapter doesn't implement required methods
 */
function ensureAdapterContract(adapter, methods, providerName) {
  if (!adapter || typeof adapter !== 'object') {
    throw new Error(`[${providerName}] Adapter must be an object`);
  }

  // Check required methods
  for (const method of methods) {
    if (typeof adapter[method] !== 'function') {
      throw new Error(`[${providerName}] Adapter missing required method: ${method}`);
    }
  }

  // Check normalization methods
  if (typeof adapter.normalizeRequestPayload !== 'function') {
    throw new Error(`[${providerName}] Adapter missing normalizeRequestPayload`);
  }

  if (typeof adapter.normalizeResponseBody !== 'function') {
    throw new Error(`[${providerName}] Adapter missing normalizeResponseBody`);
  }
}

/**
 * Resolves provider chain from options
 * @param {Object} config
 * @param {Object} config.options - Options from runtime call
 * @param {Object} config.registry - Provider registry
 * @param {Array<string>} config.defaultChain - Default chain order
 * @returns {Array<{name: string, adapter: Object}>} - Chain of providers
 */
function resolveChainProviders({ options, registry, defaultChain }) {
  let providerParam = options?.provider;

  // If no provider specified, use default chain
  if (!providerParam) {
    return defaultChain.map(name => registry[name]).filter(Boolean);
  }

  // Parse provider parameter (string or array)
  let providerNames = [];

  if (typeof providerParam === 'string') {
    // Support comma-separated string: "brasilapi,viacep"
    providerNames = providerParam.split(',').map(s => s.trim()).filter(Boolean);
  } else if (Array.isArray(providerParam)) {
    providerNames = providerParam.filter(Boolean);
  } else {
    providerNames = [String(providerParam)];
  }

  // Resolve to registry entries
  const chain = [];
  for (const name of providerNames) {
    const entry = registry[name];
    if (!entry) {
      console.warn(`[factory-helpers] Provider "${name}" not found in registry`);
      continue;
    }
    chain.push(entry);
  }

  // Fallback to default if no valid providers
  if (chain.length === 0) {
    console.warn('[factory-helpers] No valid providers, using default chain');
    return defaultChain.map(name => registry[name]).filter(Boolean);
  }

  return chain;
}

/**
 * Creates composite adapter with fallback logic
 * @param {Object} config
 * @param {Array<{name: string, adapter: Object}>} config.chain - Provider chain
 * @param {Array<string>} config.methods - Methods to implement
 * @param {Function} config.isLogicalSuccess - Success checker function
 * @param {boolean} config.continueOnNotFound - If true, try next provider even when "not found"
 * @returns {Object} - Composite adapter with all methods
 */
function createCompositeAdapter({ chain, methods, isLogicalSuccess, continueOnNotFound = false }) {
  const successChecker = isLogicalSuccess || defaultIsLogicalSuccess;
  const composite = {};

  // Create tryInOrder function for fallback logic
  async function tryInOrder(opName, fn) {
    const errors = [];
    let lastNotFoundResult = null; // Store last "not found" result to return if all fail

    // Try each provider in order
    for (const entry of chain) {
      try {
        // Call provider method
        const output = await fn(entry.adapter, entry.name);

        // Normalize response
        const normalized = entry.adapter.normalizeResponseBody(output);

        // Check logical success
        const isSuccess = successChecker(normalized);

        if (isSuccess) {
          return {
            data: normalized,
            resolvedName: entry.name,
            failedProviders: errors.length > 0 ? errors : undefined,
          };
        }

        // Check if this is a "data not found" case vs "API failure"
        if (normalized.apiWorked === true) {
          // API worked successfully, but data not found
          if (continueOnNotFound) {
            // Store the "not found" result and try next provider
            // (useful when providers have different data freshness, e.g., CNPJ)
            lastNotFoundResult = {
              data: normalized,
              resolvedName: entry.name,
              failedProviders: errors.length > 0 ? errors : undefined,
            };
            errors.push({
              provider: entry.name,
              error: normalized.error || 'Data not found',
            });
            continue;
          } else {
            // Default behavior: stop immediately (data doesn't exist)
            return {
              data: normalized,
              resolvedName: entry.name,
              failedProviders: errors.length > 0 ? errors : undefined,
            };
          }
        }

        // Provider returned, but not successful (API failure - try next)
        errors.push({
          provider: entry.name,
          error: normalized.error || 'No successful result',
        });
      } catch (err) {
        // Exception: try next provider
        errors.push({
          provider: entry.name,
          error: err.message || String(err),
        });
        continue;
      }
    }

    // All providers failed - return last "not found" result if available
    if (lastNotFoundResult) {
      return lastNotFoundResult;
    }

    // All providers failed with errors
    const errorSummary = errors.map(e => `${e.provider}: ${e.error}`).join('; ');
    throw new Error(`[${opName}] All providers failed - ${errorSummary}`);
  }

  // Create method for each operation
  for (const methodName of methods) {
    // Validate all adapters implement this method
    for (const entry of chain) {
      ensureAdapterContract(entry.adapter, [methodName], entry.name);
    }

    // Create composite method with fallback
    composite[methodName] = async function (baseOptions) {
      // Normalize request using first adapter's normalizer
      const firstAdapter = chain[0].adapter;
      const normalizedOptions = firstAdapter.normalizeRequestPayload(baseOptions);

      // Try providers in order with fallback
      return tryInOrder(methodName, async (adapter, providerName) => {
        return adapter[methodName](normalizedOptions);
      });
    };
  }

  return composite;
}

/**
 * Main factory function creator
 * @param {Object} config
 * @param {Object} config.registry - Map of providers: { providerName: { name, adapter } }
 * @param {Array<string>|string} config.methods - Methods that adapter must implement
 * @param {Function} config.isLogicalSuccess - Optional custom success validator
 * @param {boolean} config.continueOnNotFound - If true, try next provider even when "not found"
 * @returns {Function} - Factory function to be called at runtime
 */
export const createProviderFactory = config => {
  const { registry, methods, isLogicalSuccess, continueOnNotFound = false } = config;

  // Derive default chain from registry
  const defaultChain = Object.keys(registry);

  // Return factory function (called at runtime)
  return options => {
    const chainEntries = resolveChainProviders({ options, registry, defaultChain });
    const chainNames = chainEntries.map(e => e.name);
    const adapter = createCompositeAdapter({
      chain: chainEntries,
      methods: Array.isArray(methods) ? methods : [methods],
      isLogicalSuccess,
      continueOnNotFound,
    });

    return { chain: chainNames, adapter };
  };
};

export default {
  createProviderFactory,
  defaultIsLogicalSuccess,
  ensureAdapterContract,
  resolveChainProviders,
  createCompositeAdapter,
};
